import { useEffect, useState } from "react";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
	CatalogImportBulkPatch,
	CatalogImportCommit,
	CatalogImportFile,
	CatalogImportItem,
	CatalogImportItemPatch,
	CatalogImportJob,
	CatalogImportJobCreate,
	CatalogImportJobDetail,
	CatalogImportJobPatch,
	CatalogImportJobStatus,
	CatalogImportRegenerate,
	CatalogImportTextInput,
} from "@osmix/contracts";
import { api } from "@/api";

// ИИ-загрузка каталога: задания, материалы, черновики, загрузка в магазин.
// Пока задание разбирается или грузится, деталь опрашивается каждые 2 с —
// этап, done/total и расход $ шлюз пишет в задание по ходу работы.

export const catalogImportKeys = {
	all: ["catalog-import"] as const,
	jobs: (query: { applicationId?: string; shopId?: string } = {}) => ["catalog-import", "jobs", query] as const,
	jobLists: ["catalog-import", "jobs"] as const,
	job: (id: string) => ["catalog-import", "job", id] as const,
	models: ["catalog-import", "models"] as const,
	file: (jobId: string, fileId: string) => ["catalog-import", "file", jobId, fileId] as const,
};

const ACTIVE: ReadonlySet<CatalogImportJobStatus> = new Set(["parsing", "committing"]);
export const isJobActive = (status: CatalogImportJobStatus | undefined) => !!status && ACTIVE.has(status);

export function useCatalogImportJobs(query: { applicationId?: string; shopId?: string } = {}) {
	return useQuery({
		queryKey: catalogImportKeys.jobs(query),
		queryFn: () => api.catalogImport.jobs(query),
		// Список тоже живой, пока хоть одно задание в работе: статус и «загружено»
		// меняются без перезагрузки страницы.
		refetchInterval: (q) => (q.state.data?.some((job) => isJobActive(job.status)) ? 5_000 : false),
		refetchIntervalInBackground: false,
		staleTime: 10_000,
	});
}

export function useCatalogImportJob(id: string) {
	return useQuery({
		queryKey: catalogImportKeys.job(id),
		queryFn: () => api.catalogImport.job(id),
		enabled: !!id,
		staleTime: 0,
		refetchInterval: (q) => (isJobActive(q.state.data?.status) ? 2_000 : false),
		refetchIntervalInBackground: false,
	});
}

export function useCatalogImportModels() {
	return useQuery({
		queryKey: catalogImportKeys.models,
		queryFn: () => api.catalogImport.models(),
		staleTime: 10 * 60_000,
	});
}

// ── Кэш детали: правки без перечитывания всех позиций ────────────────────
// Деталь несёт до 3000 позиций — после правки одной позиции её подменяют в
// кэше, а не перечитывают задание целиком.

type Detail = CatalogImportJobDetail;

const patchDetail = (qc: QueryClient, id: string, fn: (detail: Detail) => Detail) =>
	qc.setQueryData<Detail>(catalogImportKeys.job(id), (current) => (current ? fn(current) : current));

/** Шапку задания (статус, прогресс, расход) — поверх детали, файлы и позиции остаются. */
const mergeJob = (qc: QueryClient, job: CatalogImportJob) =>
	patchDetail(qc, job.id, (detail) => ({ ...detail, ...job }));

const replaceItem = (qc: QueryClient, jobId: string, item: CatalogImportItem) =>
	patchDetail(qc, jobId, (detail) => ({
		...detail,
		items: detail.items.map((current) => (current.id === item.id ? item : current)),
	}));

const appendFiles = (qc: QueryClient, jobId: string, files: CatalogImportFile[]) =>
	patchDetail(qc, jobId, (detail) => {
		const known = new Set(detail.files.map((f) => f.id));
		return { ...detail, files: [...detail.files, ...files.filter((f) => !known.has(f.id))] };
	});

const touchLists = (qc: QueryClient) => qc.invalidateQueries({ queryKey: catalogImportKeys.jobLists });

export function useCreateImportJob() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input: CatalogImportJobCreate) => api.catalogImport.create(input),
		onSuccess: () => touchLists(qc),
	});
}

export function useUpdateImportJob(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (patch: CatalogImportJobPatch) => api.catalogImport.update(jobId, patch),
		onSuccess: (job, patch) => {
			mergeJob(qc, job);
			// Категория по умолчанию пересчитывает проблемы позиций — их надо перечитать.
			if (patch.options?.defaultCategoryId !== undefined) void qc.invalidateQueries({ queryKey: catalogImportKeys.job(jobId) });
			void touchLists(qc);
		},
	});
}

export function useCancelImportJob(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () => api.catalogImport.cancel(jobId),
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: catalogImportKeys.job(jobId) });
			void touchLists(qc);
		},
	});
}

/** Один файл за вызов (архив вернёт и всё, что из него распаковалось). */
export function useUploadImportFile(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ file, path }: { file: File; path?: string }) => api.catalogImport.uploadFile(jobId, file, path),
		onSuccess: (files) => appendFiles(qc, jobId, files),
	});
}

export function useAddImportText(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input: CatalogImportTextInput) => api.catalogImport.addText(jobId, input),
		onSuccess: (file) => appendFiles(qc, jobId, [file]),
	});
}

export function useAddImportFromApplication(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (fileIds?: string[]) => api.catalogImport.addFromApplication(jobId, fileIds?.length ? { fileIds } : {}),
		onSuccess: (files) => appendFiles(qc, jobId, files),
	});
}

export function useDeleteImportFile(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (fileId: string) => api.catalogImport.deleteFile(jobId, fileId),
		onSuccess: (_void, fileId) => {
			patchDetail(qc, jobId, (detail) => ({
				...detail,
				files: detail.files.filter((f) => f.id !== fileId && f.parentId !== fileId),
			}));
			void qc.invalidateQueries({ queryKey: catalogImportKeys.job(jobId) });
		},
	});
}

export function useParseImport(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () => api.catalogImport.parse(jobId),
		onSuccess: (job) => {
			mergeJob(qc, job);
			void qc.invalidateQueries({ queryKey: catalogImportKeys.job(jobId) });
			void touchLists(qc);
		},
	});
}

export function useUpdateImportItem(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ itemId, patch }: { itemId: string; patch: CatalogImportItemPatch }) =>
			api.catalogImport.updateItem(jobId, itemId, patch),
		onSuccess: (item) => {
			replaceItem(qc, jobId, item);
			// Счётчики в шапке задания пересчитает следующее открытие — сейчас
			// их считает сама страница по позициям.
			void qc.invalidateQueries({ queryKey: catalogImportKeys.job(jobId), refetchType: "none" });
			void touchLists(qc);
		},
	});
}

export function useBulkUpdateImportItems(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input: CatalogImportBulkPatch) => api.catalogImport.bulkUpdate(jobId, input),
		// Массовая правка не возвращает позиции — перечитываем задание.
		onSuccess: () => qc.invalidateQueries({ queryKey: catalogImportKeys.job(jobId) }),
	});
}

export function useRegenerateImportItem(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ itemId, input }: { itemId: string; input: CatalogImportRegenerate }) =>
			api.catalogImport.regenerateItem(jobId, itemId, input),
		onSuccess: (item) => {
			replaceItem(qc, jobId, item);
			// Переписывание тратит токены — расход в шапке перечитаем.
			void qc.invalidateQueries({ queryKey: catalogImportKeys.job(jobId) });
		},
	});
}

export function useCommitImport(jobId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input?: CatalogImportCommit) => api.catalogImport.commit(jobId, input),
		onSuccess: (job) => {
			mergeJob(qc, job);
			void qc.invalidateQueries({ queryKey: catalogImportKeys.job(jobId) });
			void touchLists(qc);
		},
	});
}

// ── Файлы задания: превью картинок ───────────────────────────────────────
// Оригиналы лежат в приватном бакете — байты идут через шлюз с токеном
// консоли и превращаются в objectURL. Сетка на сотни фото не должна
// открывать сотни запросов разом: не больше четырёх одновременно.

const MAX_PARALLEL = 4;
let running = 0;
const waiting: (() => void)[] = [];

const limited = async <T>(task: () => Promise<T>): Promise<T> => {
	if (running >= MAX_PARALLEL) await new Promise<void>((resolve) => waiting.push(resolve));
	running++;
	try {
		return await task();
	} finally {
		running--;
		waiting.shift()?.();
	}
};

export function useImportFileBlob(jobId: string, fileId: string | null | undefined, enabled = true) {
	return useQuery({
		queryKey: catalogImportKeys.file(jobId, fileId ?? ""),
		queryFn: () => limited(() => api.catalogImport.fileBlob(jobId, fileId ?? "")),
		enabled: enabled && !!jobId && !!fileId,
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: 5 * 60_000,
		retry: 1,
	});
}

/** objectURL для блоба; отзывается, когда блоб сменился или компонент ушёл. */
export function useObjectUrl(blob: Blob | undefined | null) {
	const [url, setUrl] = useState<string | null>(null);
	useEffect(() => {
		if (!blob) {
			setUrl(null);
			return;
		}
		const next = URL.createObjectURL(blob);
		setUrl(next);
		return () => URL.revokeObjectURL(next);
	}, [blob]);
	return url;
}

export function useImportFileUrl(jobId: string, fileId: string | null | undefined, enabled = true) {
	const query = useImportFileBlob(jobId, fileId, enabled);
	return { url: useObjectUrl(query.data), blob: query.data ?? null, isLoading: query.isLoading, error: query.error };
}
